import type { ComponentType } from "react";
import {
  Actions,
  managerCardActions,
  ModelTypeEnum,
  OrderStatus,
  UserContext,
} from "~/application/types";
import { routes } from "../theme/routes";
import type { NavMenuItemType } from "../utils/menu-functions";
import {
  SvgAccountBalanceWallet,
  SvgArrowsClockwise,
  SvgCard,
  SvgCheckCircleUnread,
  SvgConversionPath,
  SvgCurrencyExchange,
  SvgDollarSign,
  SvgExpenseReport,
  SvgPriceCheck,
  SvgRequestQuote,
  SvgAccountCircle,
  SvgHandshake,
  SvgFolderCopy,
  SvgAccountBalance,
  SvgGroups,
  SvgVoiceSelection,
  SvgShoppingCart,
  SvgArticle,
  SvgEventList,
  SvgStore,
  SvgLocalActivity,
  SvgAnalytics,
  SvgAttachMoney,
  SvgGear,
} from "~/components/Icon/icons";

type MenuItem = NavMenuItemType & {
  icon?: ComponentType;
};

export const searchMenuItems: MenuItem[] = [
  {
    title: "Aéreo",
    href: routes.Booking.Airway.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
  {
    title: "Hotéis",
    href: routes.Booking.Hotel.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
  {
    title: "Ônibus",
    href: routes.Booking.Road.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
  {
    title: "Carros",
    href: routes.Booking.Vehicle.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
  {
    title: "Adiantamento",
    href: routes.Expenses.RequestAdvance.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
  {
    title: "Despesa",
    href: routes.Expenses.SubmitExpense.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
  },
];

export const orderMenuItems: MenuItem[] = [
  {
    title: "Meus pedidos",
    href:
      routes.Orders.CustomerOrders.root + `?orderType=${ModelTypeEnum.TRAVEL}#${OrderStatus.ALL}`,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewOrder],
  },
  {
    title: "Ver pedidos",
    href: routes.Agencies.Orders.root + `?orderType=${ModelTypeEnum.TRAVEL}#${OrderStatus.ALL}`,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Relatório de acesso",
    href: routes.Configuration.Reports.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Ver pedidos",
    href:
      routes.Orders.CustomerOrders.root + `?orderType=${ModelTypeEnum.TRAVEL}#${OrderStatus.ALL}`,
    requiredContext: UserContext.Customer,
    requiredActions: [],
  },
  {
    title: "Pedidos pendentes",
    href: routes.Orders.PendingOrders.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
];

export const expensesMenuItems: MenuItem[] = [
  {
    title: "Meus adiantamentos",
    href:
      routes.Orders.CustomerOrders.root + `?orderType=${ModelTypeEnum.ADVANCE}#${OrderStatus.ALL}`,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewAdvanceOrder],
    icon: SvgCurrencyExchange,
  },
  {
    title: "Adiantamentos",
    href: routes.Expenses.AdvancesList.root + `#${OrderStatus.ALL}`,
    requiredContext: UserContext.Agency,
    requiredActions: [],
    icon: SvgCurrencyExchange,
  },
  {
    title: "Minhas despesas",
    href: routes.Expenses.ExpenseList.root + `#${OrderStatus.OPEN}`,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewExpenseReport],
    icon: SvgRequestQuote,
  },
  {
    title: "Despesas",
    href: routes.Expenses.ExpenseList.root + `#${OrderStatus.OPEN}`,
    requiredContext: UserContext.Agency,
    requiredActions: [],
    icon: SvgRequestQuote,
  },
  {
    title: "Prestação de contas",
    href:
      routes.Orders.CustomerOrders.root +
      `?orderType=${ModelTypeEnum.EXPENSE_REPORT}#${OrderStatus.ALL}`,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewExpenseReport],
    icon: SvgPriceCheck,
  },
  {
    title: "Relatórios",
    href: routes.Expenses.ExpenseReportsList.root + `#${OrderStatus.ALL}`,
    requiredContext: UserContext.Agency,
    requiredActions: [],
    icon: SvgPriceCheck,
  },
  {
    title: "Despesa por rota",
    href: routes.Expenses.KmExpense.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.CreateOrder],
    icon: SvgConversionPath,
  },
  {
    title: "Pendências financeiras",
    href: routes.Expenses.FinancialPendings.root,
    requiredContext: UserContext.Customer,
    requiredActions: [],
    icon: SvgCheckCircleUnread,
  },
];

export const settingsMenuItems: MenuItem[] = [
  {
    title: "Parâmetros",
    href: routes.Configuration.Parameters.Customers.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewSettingParameter],
    icon: SvgAnalytics,
  },
  {
    title: "Dashboard BI",
    href: routes.DashboardBIV2.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Empresas",
    href: routes.Configuration.Customers.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Dados bancários",
    href: routes.Configuration.BankAccounts.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewBankAccount],
    icon: SvgAccountBalance,
  },
  {
    title: "Credenciais",
    href: routes.Configuration.Credentials.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Cartões de crédito",
    href: routes.Configuration.CreditCards.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Taxas",
    href: routes.Configuration.Fees.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Markups",
    href: routes.Configuration.Markups.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Markdowns",
    href: routes.Configuration.Markdowns.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Agências",
    href: routes.Configuration.Agencies.root,
    requiredContext: UserContext.Biztrip,
    requiredActions: [],
  },
  {
    title: "Categorias de despesas",
    href: routes.Configuration.ExpenseCategories.root,
    requiredContext: UserContext.Biztrip,
    requiredActions: [],
  },
  {
    title: "Filiais",
    href: routes.Configuration.Branches.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewBranch],
    icon: SvgStore,
  },
  {
    title: "Modelos de aprovação",
    href: routes.Configuration.ApprovalModels.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewApprovalModel],
    icon: SvgHandshake,
  },
  {
    title: "Centros de custo",
    href: routes.Configuration.CostCenters.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewCostCenter],
    icon: SvgAccountBalance,
  },
  {
    title: "Projetos",
    href: routes.Configuration.Projects.root,
    requiredActions: [Actions.ViewProject],
    requiredContext: UserContext.Customer,
    icon: SvgFolderCopy,
  },
  {
    title: "Funcionários",
    href: routes.Configuration.CustomerEmployees.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewCustomerEmployee],
    icon: SvgAccountCircle,
  },
  {
    title: "Grupos de funcionários",
    href: routes.Configuration.CustomerEmployeesGroups.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewCustomerEmployeesGroup],
    icon: SvgGroups,
  },
  {
    title: "Orçamentos",
    href: routes.Configuration.Budgets.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewBudget],
    icon: SvgAttachMoney,
  },
  {
    title: "Políticas de adiantamentos",
    href: routes.Configuration.PoliciesAdvances.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewPolicy],
    icon: SvgCurrencyExchange,
  },
  {
    title: "Políticas de despesas",
    href: routes.Configuration.PoliciesExpenses.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewPolicy],
    icon: SvgRequestQuote,
  },
  {
    title: "Motivos de viagem",
    href: routes.Configuration.ReasonTrips.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewReasonTrip],
    icon: SvgVoiceSelection,
  },
  {
    title: "Justificativas",
    href: routes.Configuration.Justifications.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewJustification],
    icon: SvgArticle,
  },
  {
    title: "Tipo de despesa",
    href: routes.Configuration.ExpenseTypes.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.GetExpenseType],
    icon: SvgEventList,
  },
  {
    title: "Políticas de compra",
    href: routes.Configuration.PurchasingPolicies.root,
    requiredActions: [Actions.ViewPolicyValue],
    requiredContext: UserContext.Customer,
    icon: SvgShoppingCart,
  },
  {
    title: "Hotéis offline",
    href: routes.Configuration.OfflineHotels.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Categorias de custo",
    href: routes.Configuration.CostCategories.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Regras tarifárias aéreas",
    href: routes.Configuration.AirwayRules.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Gerenciar erros de faturas",
    href: routes.Configuration.WintourErrors.root,
    requiredContext: UserContext.Agency,
    requiredActions: [],
  },
  {
    title: "Gestão de bilhetes",
    href: routes.Configuration.UnflownAirways.root,
    requiredContext: UserContext.Customer,
    requiredActions: [],
    icon: SvgLocalActivity,
  },
];
export const expenseCardMenuItems: MenuItem[] = [
  {
    title: "Cartões",
    href: routes.ExpenseCards.root,
    requiredContext: UserContext.Customer,
    requiredActions: managerCardActions,
    icon: SvgAccountBalanceWallet,
  },
  {
    title: "Meus cartões",
    href: routes.ExpenseCards.UserExpenseCards.root,
    requiredContext: UserContext.Customer,
    requiredActions: [],
    icon: SvgCard,
  },
];

export const reportingCenterMenuItems: MenuItem[] = [
  {
    title: "Relatórios de viagens",
    href: routes.ReportingCenter.TravelReports.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewPurchaseReport],
    icon: SvgArrowsClockwise,
  },
  {
    title: "Relatórios de despesas",
    href: routes.ReportingCenter.ExpenseReports.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewPurchaseReport],
    icon: SvgExpenseReport,
  },
  {
    title: "Relatórios de faturamento",
    href: routes.ReportingCenter.BillingReports.root,
    requiredContext: UserContext.Customer,
    requiredActions: [Actions.ViewPurchaseReport],
    icon: SvgDollarSign,
  },
];

export const notificationsMenuItems: MenuItem[] = [
  {
    title: "Comunicados do sistema",
    href: routes.Notifications.SystemAnnouncements.root,
    requiredContext: UserContext.Biztrip,
    requiredActions: [],
    icon: SvgGear,
  },
];
